/**
 * Promo Table Component
 * 
 * Displays promos in a table format with:
 * - Promo code and name
 * - Campaign name
 * - Domain and transaction type
 * - Benefit (discount information)
 * - Usage metrics
 * - Status badges
 * - Action buttons (Edit, Archive, Activate/Deactivate)
 * - Pagination
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Table } from 'react-bootstrap';
import { Promo, PromoStatus, PromoVisibilityType } from '../../../../api/promos/types';
import { PromoStatusBadge } from '../../../../Common/BadgeStyles';
import '../Promos.css';

interface PromoTableProps {
  promos: Promo[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit?: (promo: Promo) => void;
  onView: (promo: Promo) => void;
  onDelete?: (promo: Promo) => void;
  onArchive?: (promo: Promo) => void;
  onToggleStatus?: (promo: Promo) => void;
  onDuplicate?: (promo: Promo) => void;
  onManageAssignments?: (promo: Promo) => void;
}

const PromoTable: React.FC<PromoTableProps> = React.memo(({
  promos,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onView,
  onDelete,
  onArchive,
  onToggleStatus,
  onDuplicate,
  onManageAssignments,
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const actionButtonRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({});
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  const handleDropdown = (promoId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (openDropdown === promoId) {
      setOpenDropdown(null);
      setDropdownPosition(null);
      return;
    }
    
    const buttonElement = event.currentTarget as HTMLSpanElement;
    if (!buttonElement) return;
    
    const parentRow = buttonElement.closest('tr');
    if (parentRow) {
      const rowPromoId = parentRow.getAttribute('data-promo-id');
      if (rowPromoId && rowPromoId !== promoId) {
        return;
      }
    }
    
    const buttonPromoId = buttonElement.getAttribute('data-promo-action-id');
    if (buttonPromoId && buttonPromoId !== promoId) {
      return;
    }
    
    actionButtonRefs.current[promoId] = buttonElement;
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 200;
    const dropdownWidth = 120;
    
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    let xPosition = rect.right - dropdownWidth;
    if (xPosition < 8) {
      xPosition = rect.left;
    }
    if (xPosition + dropdownWidth > viewportWidth - 8) {
      xPosition = viewportWidth - dropdownWidth - 8;
    }
    
    let yPosition: number;
    if (spaceBelow >= dropdownHeight) {
      yPosition = rect.bottom + 4;
    } else if (spaceAbove >= dropdownHeight) {
      yPosition = rect.top - dropdownHeight - 4;
    } else {
      const maxY = Math.max(8, viewportHeight - dropdownHeight - 8);
      yPosition = Math.min(rect.bottom + 4, maxY);
    }
    
    const newPosition = { x: xPosition, y: yPosition };
    setOpenDropdown(promoId);
    setDropdownPosition(newPosition);
  };
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown) {
        const dropdownRef = dropdownRefs.current[openDropdown];
        const buttonRef = actionButtonRefs.current[openDropdown];
        
        if (
          dropdownRef &&
          !dropdownRef.contains(event.target as Node) &&
          buttonRef &&
          !buttonRef.contains(event.target as Node)
        ) {
          setOpenDropdown(null);
          setDropdownPosition(null);
        }
      }
    };
    
    const handleScroll = () => {
      if (openDropdown) {
        const buttonRef = actionButtonRefs.current[openDropdown];
        if (buttonRef) {
          requestAnimationFrame(() => {
            const rect = buttonRef.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const dropdownHeight = 200;
            const dropdownWidth = 120;
            
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            let xPosition = rect.right - dropdownWidth;
            if (xPosition < 8) {
              xPosition = rect.left;
            }
            if (xPosition + dropdownWidth > viewportWidth - 8) {
              xPosition = viewportWidth - dropdownWidth - 8;
            }
            
            let yPosition: number;
            if (spaceBelow >= dropdownHeight) {
              yPosition = rect.bottom + 4;
            } else if (spaceAbove >= dropdownHeight) {
              yPosition = rect.top - dropdownHeight - 4;
            } else {
              const maxY = Math.max(8, viewportHeight - dropdownHeight - 8);
              yPosition = Math.min(rect.bottom + 4, maxY);
            }
            
            setDropdownPosition({ x: xPosition, y: yPosition });
          });
        } else {
          setOpenDropdown(null);
          setDropdownPosition(null);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [openDropdown]);
  
  const formatBenefit = (benefit: Promo['benefit']) => {
    if (benefit.type === 'PERCENTAGE_DISCOUNT') {
      return `${benefit.value}% OFF`;
    } else if (benefit.type === 'FIXED_DISCOUNT') {
      return `₹${benefit.value} OFF`;
    } else if (benefit.type === 'FREE_SHIPPING') {
      return 'Free Shipping';
    }
    return '-';
  };
  
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  const totalPages = Math.ceil(totalCount / pageSize);
  
  return (
    <div className="promo-table-wrapper">
      <Table responsive className="promo-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Campaign</th>
            <th>Promo Code</th>
            <th>Domain</th>
            <th>Benefit</th>
            <th className="text-center">Visibility</th>
            <th className="text-center">Usage</th>
            <th>Status</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {promos.map((promo) => (
            <tr key={promo.id} data-promo-id={promo.id}>
              {/* Name */}
              <td>
                <div className="promo-name">
                  <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => onView(promo)}
                    style={{ color: '#006EFF', fontWeight: 400 }}
                  >
                    {promo.name || '-'}
                  </button>
                </div>
              </td>
              
              {/* Campaign */}
              <td>
                <span style={{ fontSize: '13px', color: '#495057' }}>
                  {promo.campaignName || '-'}
                </span>
              </td>
              
              {/* Promo Code */}
              <td>
                <div className="promo-code">
                  {promo.code ? (
                    <code style={{ 
                      backgroundColor: '#F3F4F6', 
                      padding: '4px 8px', 
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: '#1F2937',
                      fontWeight: 500,
                    }}>
                      {promo.code}
                    </code>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#495057' }}>-</span>
                  )}
                </div>
              </td>
              
              {/* Domain */}
              <td>
                <span style={{ fontSize: '13px', color: '#495057' }}>
                  {promo.domain}
                </span>
              </td>
              
              {/* Benefit */}
              <td>
                <span style={{ fontSize: '13px', color: '#1F2937', fontWeight: 500 }}>
                  {formatBenefit(promo.benefit)}
                </span>
              </td>

              {/* Visibility */}
              <td className="text-center">
                {promo.visibility?.type === 'ASSIGNED' ? (
                  <span className="badge badge-visibility-assigned">Assigned</span>
                ) : promo.visibility?.type === 'PUBLIC' ? (
                  <span className="badge badge-visibility-public">Public</span>
                ) : (
                  <span style={{ fontSize: '13px', color: '#374151', fontWeight: 400 }}>-</span>
                )}
              </td>
              
              {/* Usage */}
              <td className="text-center">
                <span style={{ fontWeight: 400 }}>
                  {promo.totalUsage ? promo.totalUsage.toLocaleString() : '0'}
                </span>
              </td>
              
              {/* Status */}
              <td>
                <PromoStatusBadge status={promo.status as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'EXPIRED' | 'INACTIVE'} />
              </td>
              
              {/* Actions - 3 dots dropdown (only when mutating callbacks are provided) */}
              <td style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                {(onEdit || onToggleStatus || onDelete || onArchive || onDuplicate) && (
                  <>
                    <span
                      className="actions-icon"
                      title="Actions"
                      data-promo-action-id={promo.id}
                      onClick={(e) => handleDropdown(promo.id, e)}
                      ref={(el) => {
                        if (el) {
                          actionButtonRefs.current[promo.id] = el;
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        fontSize: '20px',
                        color: '#6B7280',
                        display: 'inline-block',
                        lineHeight: '1',
                        userSelect: 'none',
                      }}
                    >
                      &#8942;
                    </span>
                    {openDropdown === promo.id && dropdownPosition && (
                      createPortal(
                        <div
                          className="dropdown-menu-actions"
                          ref={(el) => {
                            if (el) {
                              dropdownRefs.current[promo.id] = el;
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'fixed',
                            top: `${dropdownPosition.y}px`,
                            left: `${dropdownPosition.x}px`,
                            zIndex: 9999,
                          }}
                        >
                          <div
                            className="dropdown-item"
                            onClick={() => {
                              onView(promo);
                              setOpenDropdown(null);
                              setDropdownPosition(null);
                            }}
                          >
                            View
                          </div>
                          {onEdit && (
                            <div
                              className="dropdown-item"
                              onClick={() => {
                                onEdit(promo);
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              Edit
                            </div>
                          )}

                          {promo.visibility?.type === PromoVisibilityType.ASSIGNED && onManageAssignments && (
                            <div
                              className="dropdown-item"
                              onClick={() => {
                                onManageAssignments(promo);
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              Manage assignments
                            </div>
                          )}
                          {false && onDuplicate && (
                            <div
                              className="dropdown-item"
                              onClick={() => {
                                onDuplicate && onDuplicate(promo);
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              Duplicate
                            </div>
                          )}
                          {onToggleStatus && (
                            <div
                              className="dropdown-item"
                              onClick={() => {
                                onToggleStatus(promo);
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              {promo.status === PromoStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                            </div>
                          )}
                          {/* Show Delete only for DRAFT status */}
                          {promo.status === PromoStatus.DRAFT && (onArchive || onDelete) && (
                            <div
                              className="dropdown-item archive"
                              onClick={() => {
                                if (onArchive) {
                                  onArchive(promo);
                                } else if (onDelete) {
                                  onDelete(promo);
                                }
                                setOpenDropdown(null);
                                setDropdownPosition(null);
                              }}
                            >
                              Delete
                            </div>
                          )}
                        </div>,
                        document.body
                      )
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
});

PromoTable.displayName = 'PromoTable';

export default PromoTable;
